import { useVirtualizer } from '@tanstack/react-virtual';

// Windows table rows: only the visible slice mounts, with spacer <tr>s holding
// scroll height. `children(index, measureRef)` renders one row; attach
// `ref={measureRef}` and `data-index={index}` to its <tr>.
//
// Fallback: when the scroll element has no measurable height (jsdom in tests, or
// a hidden tab), the virtualizer yields 0 items — we then render ALL rows so
// content is never silently missing.
export default function VirtualTableRows({
  count,
  scrollRef,
  colSpan,
  estimateSize = 36,
  overscan = 12,
  children,
}) {
  const virtualizer = useVirtualizer({
    count,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimateSize,
    overscan,
    // First paint (before measurement) assumes an ~800px viewport so we window
    // immediately instead of briefly rendering every row.
    initialRect: { width: 1200, height: 800 },
  });

  const items = virtualizer.getVirtualItems();

  if (count > 0 && items.length === 0) {
    // Unmeasurable viewport — render all rows (no measureRef needed).
    const all = [];
    for (let i = 0; i < count; i++) all.push(children(i, undefined));
    return all;
  }

  const paddingTop = items.length ? items[0].start : 0;
  const paddingBottom = items.length
    ? virtualizer.getTotalSize() - items[items.length - 1].end
    : 0;

  return (
    <>
      {paddingTop > 0 && (
        <tr aria-hidden="true" style={{ height: paddingTop }}>
          <td colSpan={colSpan} style={{ padding: 0, border: 0 }} />
        </tr>
      )}
      {items.map((vi) => children(vi.index, virtualizer.measureElement))}
      {paddingBottom > 0 && (
        <tr aria-hidden="true" style={{ height: paddingBottom }}>
          <td colSpan={colSpan} style={{ padding: 0, border: 0 }} />
        </tr>
      )}
    </>
  );
}
